import type { OrderProductDiscount } from "./Deals";
import type { Distributor, DistributorInfo, DualProductDistributor } from "./Distributor";
import type { PaidSponsorTag } from "./PaidSponsorTag";
import type { PaidTrackingValues } from "./PaidTrackingValues";
import type { DiscountResult } from "@retailer/services/discounts_service";
import type { ProductListingLayout } from "@retailer/svelte/ProductLines/ProductLineContainer/stores/ProductLineListingStore";

export interface PLDistributor {
  always_choose_distributor?: boolean;
  allow_ordering_out_of_stock: boolean;
  contact_phone?: string;
  id: number;
  integrated?: boolean;
  name?: string;
  no_sales_reps_needed?: boolean;
  seven_fifty_integration?: boolean;
  sf_requires_a_price_to_order_a_product?: null | boolean;
  state?: string | null;
  uses_price_zones_for_discounts?: boolean;
  uses_price_zones?: boolean;
  uses_warehouse_pickup?: boolean;
  uses_wine_cellar?: boolean;
}

export interface Tag {
  name?: string;
  tag_type?: string;
  tag_sub_type?: string;
  hidden?: boolean;
  primary?: boolean;
  priority?: string;
  id?: number;
}

export interface BrandFamily {
  id: number;
  name: string;
}

export interface MiniProductLine {
  distributor: { id: number };
}

export interface ProductLine {
  abv: string | null;
  abv_range: string | null;
  bestDistributorTier?: number;
  brand: string | null;
  brand_family_id?: number | null;
  brand_family_name?: string | null;
  category_id: number;
  cloudinary_image_url: string | null;
  created_by_distributor_id: number | null;
  description: string | null;
  discontinued: boolean | null;
  distributor_info: DistributorInfo;
  distributors: Distributor[];
  dual_product_distributors?: DualProductDistributor[];
  future_prices_available: boolean | null;
  ends_at: string | null;
  editing?: boolean;
  has_no_image: boolean;
  id: number;
  image_name: string | null;
  image_version: string | null;
  manufacturer_id: number;
  manufacturer_name?: string;
  name: string;
  needs_review: boolean;
  non_alcoholic: boolean;
  num_ratings: string | null;
  pairs_well_with: string | null;
  product_type_id: number;
  rating: string | null;
  sf_description: string | null;
  star_rating: string | null;
  starts_at: string | null;
  state: string | null;
  subcategory_id: number;
  tasting_notes: string | null;
  distributor?: PLDistributor | null;
  has_discount?: boolean;
  is_paid: boolean;
  paid_tracking_values?: PaidTrackingValues;
  paid_sponsor_tags?: PaidSponsorTag[];
  tags?: Tag[];
  wine_appellation?: { id: number; name: string };
  wine_country?: { id: number; name: string };
  wine_region?: { id: number; name: string };
  wine_varietal?: { name: string };
  wine_varietals?: { d: number; name: string; display_order: number }[];
  brand_family?: BrandFamily;
  products?: Product[];
  product_line_id?: number;
  times_ordered_count?: number;
  category_name: string;
  subcategory_name: string;
  product_type_name: string;
  retailer_orders_from_class_b?: boolean;
  price_bucket?: number;
  model_name?: string;
  model_version?: string;
  manufacturer?: string;
  sell_sheet_products_attributes: any[];
  vintage_years: any;
  category?: string;
  additional_notes?: string;
  cloned_from_product_line_id?: number;
  dual_product?: boolean;
  sf_product_version_id?: number;
  sf_price_quotes?: ProductLinePriceQuote[];
  sales_divisions?: SalesDivision[];
}

export interface InventoryPrice {
  inventory_id: number;
  case_price?: number;
  unit_price?: number;
  discounted_case_price?: number;
  discounted_unit_price?: number;
  container_case_size?: number;
  unit_container?: string;
  container_type?: string;
  product_id: number;
  product_line_id: number;
  sold_by?: string;
  distributor_id: number;
  container_size?: string;
  container_units?: string;
  has_custom_price?: boolean;
}

export interface ProductLinePriceQuote {
  label: string;
  price_per_unit?: number;
  price_per_case?: number;
  container_type: string;
}

export interface Allocation {
  is_allocated: boolean;
  amount: number;
}

export interface Inventory {
  id: number;
  sku: string | null;
  case_sku: string | null;
  unit_sku: string | null;
  sold_by: string | null;
  orderable?: boolean;
  hidden?: boolean;
  distributor_id?: number;
  unit_price?: number;
  vendor_product_version_id?: number;
  product_id?: number;
  sf_product_id?: number;
  vendor_product_id?: number;
}

export interface CartWidgetProduct {
  id: number;
  distributor_id?: number | null;
  allocation?: Allocation | null;
  inventory?: Inventory | CartProductInventory;
  inventory_below_minimum: boolean;
  inventory_is_low_stock?: boolean;
  inventory_is_completely_out_of_stock?: boolean;
  inventory_id: number;
  container_type: string;
  container_case_size: number;
  full_sleeve_only: boolean;
  case_price?: number;
  unit_price?: number;
  verified_in_stock?: boolean;
}

export interface Product {
  abv: number | null;
  allocation?: Allocation | null;
  amount_type?: string;
  bottle_tops?: boolean | null;
  case_gtin: string | null;
  case_price: number;
  cloudinary_image_url: string | null;
  container_carton_size: number | null;
  container_case_size: number | null;
  container_size: string | null;
  container_type: string | null;
  container_units: string | null;
  custom_price_id?: number | null;
  description?: string;
  discount_override_image?: string | null;
  discounted_case_price?: number | null;
  discounted_unit_price?: number | null;
  distributor_id?: number | null;
  distributor_info?: {
    id: number;
    name?: string | null;
    uses_warehouse_pickup?: boolean | null;
    allow_ordering_out_of_stock: boolean;
    enforce_allocation_amounts?: boolean | null;
    require_account_number_for_add_to_cart?: boolean | null;
    sf_requires_a_price_to_order_a_product?: boolean | null;
  };
  full_sleeve_only: boolean;
  distributor?: any | null;
  has_custom_price: boolean | null;
  has_integrated_price: boolean | null;
  hidden?: boolean;
  id: number;
  image_upload_id: number | null;
  image_version: number | null;
  inventory?: Inventory;
  inventory_below_minimum: boolean;
  inventory_is_low_stock?: boolean;
  inventory_is_completely_out_of_stock?: boolean;
  inventory_id: number | null;
  limited_ordering_product?: {
    approval_status?: boolean;
    sold_by?: string;
  };
  matched_distributor_id: number | null;
  material: string | null;
  name: string | null;
  needs_review: boolean;
  pack_gtin: string | null;
  packaging_description?: string | null;
  pairs_well_with?: string | null;
  permanent_deal_notes?: { notes: string } | null;
  product_id?: number;
  rating?: string | null;
  recently_ordered: boolean;
  sf_description: string | null;
  sku?: string | null;
  tasting_notes?: string | null;
  times_ordered_count: number;
  unit_gtin: string | null;
  unit_price: number;
  vintage_year?: number | null;
  wine_appellation?: { id: number; name: string };
  wine_country?: { id: number; name: string };
  wine_region?: { id: number; name: string };
  wine_varietal?: { name: string };
  orderable?: boolean | null;
  sold_by?: string | null;
  addingToCart?: string | null;
  product_line?: ProductLine;
  product_line_id?: number;
  product_line_name?: string;
  discount_string?: string;
  scheduleLimit?: number;
  paid_tracking_values?: PaidTrackingValues;
  stock_status?: "In stock" | "Low stock" | "Out of stock" | "No recent inventory update";
}

// the kind of product coming from the api for list products, collection entries
export interface CartProduct {
  allocation?: Allocation | null;
  amount_type?: string;
  available?: boolean;
  backordered?: boolean;
  carrier_deposit?: number;
  case_diff?: number;
  case_price_at_purchase: number;
  case_price: number;
  case_quantity?: number;
  container_deposit?: number;
  container_size?: string;
  container_type?: null;
  container_units?: string;
  custom_case_price?: number;
  custom_price_id?: number;
  custom_unit_price?: number;
  deal_notes?: string;
  deleted?: boolean;
  deposit_included_in_price?: boolean;
  discount_amount?: number;
  discounted_case_price?: number;
  discount_string?: string;
  discounted_unit_price?: number;
  discounts?: OrderProductDiscount[];
  distributor_id?: number | null;
  distributor?: PLDistributor;
  full_sleeve_only: boolean;
  has_custom_price?: boolean;
  has_integrated_price?: boolean;
  id?: number;
  inventory_below_minimum?: boolean;
  inventory_id: number | null;
  inventory_is_completely_out_of_stock?: boolean;
  inventory_is_low_stock?: boolean;
  inventory: Inventory | CartProductInventory;
  is_available?: boolean;
  keg_deposit?: number;
  limited_ordering_product?: {
    sold_by?: string;
  };
  manufacturer: CartProductManufacturer;
  matched_distributor_id?: number | null;
  order_id?: number;
  orderable?: boolean;
  order_product_discount_source_id?: number;
  original_case_quantity?: number;
  original_subtotal?: number;
  original_total?: number;
  original_unit_quantity?: number;
  out_of_stock?: boolean;
  permanent_deal_notes?: {
    notes: string;
    id?: number;
  };
  permanentDeal?: boolean;
  product_id: number;
  product_line: ProductLine | CartProductProductLine;
  product: Product | CartProductProduct;
  removed_by_rep?: boolean;
  removePermanentDeal?: boolean;
  retailer_inventory?: any | null;
  requested_item: boolean;
  saved?: boolean;
  sku?: string;
  subtotal?: number;
  total?: number;
  unit_diff?: number;
  unit_price_at_purchase: number;
  unit_price: number;
  unit_quantity?: number;
  verified_in_stock?: boolean;
  warehouse_id?: number | null;
  warehouse_name?: string;
  warehouse_location_code?: string;
  stock_status?: "In stock" | "Low stock" | "Out of stock" | "No recent inventory update";
  status?: number;
}

export interface Category {
  id: number;
  name: string;
  product_type_id: number;
}

export interface Container {
  container_type: string;
}

export interface Subcategory {
  category_id: number;
  id: number;
  name: string;
}

export interface ProductType {
  id: number;
  name: string;
  case: boolean;
  unit: boolean;
  identifier?: string;
}

export interface Country {
  id: number;
  name: string;
}

export interface Region {
  id: number;
  name: string;
}

export interface LimitedOrderingProduct {
  group_director_id?: number;
  note?: string;
}

export interface QuickOrderListProduct {
  id: number;
  full_count: number;
  named_placeholder: number | null;
  position: number;
  product_line_placeholder: number | null;
  source_list_id: number;
  product: Product;
  distributor_name?: string;
}

export interface ProductCardDistributorInfo {
  id?: number;
  name?: string;
  integrated?: boolean;
  hideDistributor?: boolean;
}

export enum ProductListAccordionState {
  Open = "open",
  Expanded = "expanded",
}

export interface ProductCardInventoryStatus {
  inventory_id: number;
  product_id: number;
  verified_in_stock: boolean;
}

export interface SalesDivision {
  id: number;
  name: string;
  distributorId: number;
}

export interface ProductLineListingStoreContents {
  accordionStatesParamsKey: string | null;
  discountData: { [key: number]: DiscountResult[] };
  filtersCount: number;
  listViewAccordionStates: { [key: number]: ProductListAccordionState };
  loadingPrices: boolean;
  mobileFiltersVisible: boolean;
  onApplyFilters: any;
  onClearAllFilters: any;
  onClearFilter: any;
  productData: { [key: number]: Product[] };
  productLineFuturePrices: { [key: number]: any };
  inventoryPrices: InventoryPrice[];
  productListLayout: ProductListingLayout;
  productListLimit: number;
  productLines?: ProductLine[];
  productsFound: boolean;
  selectedFilters: any;
}

export type CartProductProduct = {
  bottle_tops: boolean | null;
  container_carton_size: number | null;
  container_case_size: number | null;
  container_size: string;
  container_type: string;
  container_units: string;
  id: number;
  material: string | null;
  name: string;
  packaging_description: string | null;
  vintage_year: number | null;
};

export type CartProductProductLine = {
  abv_range: string | null;
  abv: string | null;
  brand_family_id: number | null;
  brand: string | null;
  category_id: number;
  cloudinary_image_url: string | null;
  country: string | null;
  description: string | null;
  ends_at: string | null;
  has_no_image: boolean;
  id: number;
  image_name: string | null;
  image_version: string | null;
  manufacturer_id: number | null;
  name: string;
  non_alcoholic: boolean;
  num_ratings: string | null;
  pairs_well_with: string | null;
  product_type_id: number;
  product_type_name: string;
  rating: string | null;
  sf_description: string | null;
  star_rating: string | null;
  starts_at: string | null;
  state: string | null;
  subcategory_id: number;
  tasting_notes: string | null;
  times_ordered_count: number;
};

export type CartProductInventory = {
  hidden: boolean;
  id: number;
  orderable: boolean;
  sku: string | null;
  sold_by: string | null;
};

export type CartProductManufacturer = {
  id: number;
  name: string;
};

export type CartProductAllocation = {
  allocation_message: string | null;
  amount: number | null;
  is_allocated: boolean;
  zero_allocation_message: string | null;
};
